import { Controller } from '@hotwired/stimulus'
import bootstrap from 'bootstrap/dist/js/bootstrap'

export default class extends Controller {
  static values = { url: String, interactive: Boolean }

  connect() {
    this.popover = bootstrap.Popover.getOrCreateInstance(this.element)
    this.loading = false

    if (this.hasUrlValue) {
      this.element.addEventListener('show.bs.popover', this.loadContent.bind(this))
    }

    if (this.interactiveValue) {
      this.connectInteractive()
    }
  }

  disconnect() {
    this.disconnectInteractive()

    if (this.popover) {
      this.popover.dispose()
      this.popover = null
    }
  }

  // Hover-open + click-pin behavior for popovers that contain a link. Bootstrap's
  // native `hover click` trigger hides the popover on mouseleave before the pointer
  // can reach the link, so we drive a `manual`-trigger popover ourselves: hover
  // shows it, moving into the popover keeps it open, click pins it until an outside
  // click or Escape.
  connectInteractive() {
    this.pinned = false
    this.open = false
    this.hideTimer = null

    this.onElementEnter = () => {
      this.cancelHide()
      this.showPopover()
    }
    this.onElementLeave = () => this.scheduleHide()
    this.onElementClick = () => {
      this.pinned = true
      this.cancelHide()
      this.showPopover()
    }
    // Document listeners only while open: every menu item carries one of these controllers, so
    // registering at connect ran one closure per item on every click and keypress on the page. They go on at
    // show, not shown: a tap outside during the fade was missed and left the popover pinned open.
    this.onShow = () => {
      this.open = true
      document.addEventListener('click', this.onDocumentClick)
      document.addEventListener('keydown', this.onDocumentKeydown)
    }
    this.onShown = () => this.bindTip()
    this.onHidden = () => {
      this.unbindDocument()
      this.unbindTip()
      this.pinned = false
      // On hidden, not hide: a show() during the fade is undone by the fade's own completion, which
      // would leave the flag set with no popover and the button dead.
      this.open = false
    }
    this.onDocumentClick = (event) => {
      if (this.element.contains(event.target)) return
      if (this.tipElement()?.contains(event.target)) return
      this.unpinAndHide()
    }
    this.onDocumentKeydown = (event) => {
      if (event.key === 'Escape') this.unpinAndHide()
    }

    this.element.addEventListener('mouseenter', this.onElementEnter)
    this.element.addEventListener('mouseleave', this.onElementLeave)
    this.element.addEventListener('click', this.onElementClick)
    this.element.addEventListener('show.bs.popover', this.onShow)
    this.element.addEventListener('shown.bs.popover', this.onShown)
    this.element.addEventListener('hidden.bs.popover', this.onHidden)
  }

  disconnectInteractive() {
    if (!this.interactiveValue) return

    this.cancelHide()
    this.unbindTip()
    this.unbindDocument()
    this.element.removeEventListener('mouseenter', this.onElementEnter)
    this.element.removeEventListener('mouseleave', this.onElementLeave)
    this.element.removeEventListener('click', this.onElementClick)
    this.element.removeEventListener('show.bs.popover', this.onShow)
    this.element.removeEventListener('shown.bs.popover', this.onShown)
    this.element.removeEventListener('hidden.bs.popover', this.onHidden)
  }

  // Bootstrap 5.2 has no guard on show(): run on an open popover, its completion sees the reset hover
  // flag and hides it, so hover-then-click (and a tap, which fires both) closed the popover it pinned.
  showPopover() {
    if (this.open) return
    this.popover.show()
  }

  // Safe when nothing is registered (removeEventListener is a no-op), which covers disconnecting
  // while the popover is still open.
  unbindDocument() {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
  }

  // Resolve the visible popover element via aria-describedby (public/stable in
  // BS 5.2.3) and keep it open while the pointer is inside it.
  bindTip() {
    this.tip = this.tipElement()
    if (!this.tip) return

    this.onTipEnter = () => this.cancelHide()
    this.onTipLeave = () => this.scheduleHide()
    this.tip.addEventListener('mouseenter', this.onTipEnter)
    this.tip.addEventListener('mouseleave', this.onTipLeave)
  }

  tipElement() {
    const id = this.element.getAttribute('aria-describedby')
    return id ? document.getElementById(id) : null
  }

  unbindTip() {
    if (this.tip) {
      this.tip.removeEventListener('mouseenter', this.onTipEnter)
      this.tip.removeEventListener('mouseleave', this.onTipLeave)
      this.tip = null
    }
  }

  scheduleHide() {
    if (this.pinned) return
    this.cancelHide()
    this.hideTimer = window.setTimeout(() => this.popover.hide(), 200)
  }

  cancelHide() {
    if (this.hideTimer) {
      window.clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  unpinAndHide() {
    this.pinned = false
    this.cancelHide()
    this.popover.hide()
  }

  loadContent() {
    // Prevent multiple concurrent requests
    if (this.loading) return
    this.loading = true

    // Get fresh popover instance - the cached reference may be stale after modal interactions
    const popover = bootstrap.Popover.getOrCreateInstance(this.element)

    fetch(this.urlValue, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        popover.setContent({ '.popover-body': html })
      })
      .catch(error => {
        console.error('Error loading popover content:', error)
        popover.setContent({ '.popover-body': 'Error loading content' })
      })
      .finally(() => {
        this.loading = false
      })
  }
}
